// CastPanel — the semantic casting surface (Phase 3). Given an arc (circleKey) and optionally a
// source-persona description, it runs cast search, shows RANKED character candidates with similarity
// scores + matched attributes, and commits a cast (writes arc_castings + the vms_castings judgment
// index). It always offers "Create a character" — primary when nothing matches, secondary otherwise —
// pre-filled from the source persona, so an operator can cast the character an arc needs in one place.
//
// Reuses the app's ui kit (Modal/Card/Button/Badge/Select/Textarea) and react-query, matching the
// patterns in AdminCharactersPage.

import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal.tsx";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { Select } from "@/components/ui/Select.tsx";
import { Textarea } from "@/components/ui/Textarea.tsx";
import { researchApi, type CastCandidate } from "@/api/research.ts";
import { useToast } from "@/stores/toastStore.ts";

// Source persona seed — what a teardown extracts (or the operator types). Drives search + prefilled create.
export interface SourcePersonaSeed {
  description?: string;
  gender?: string | null;
  ageRange?: string | null;
  platform?: string | null;
  capability?: "talking_head" | "motion" | null;
}

interface CastPanelProps {
  circleKey: string;
  arcLabel?: string;                       // human label for the arc, for the header
  seed?: SourcePersonaSeed;                // prefill from a teardown's source persona (optional)
  videoUrl?: string;                       // if set with platform, for-arc auto-derives candidates
  platform?: string;
  onClose: () => void;
  onCreateCharacter?: (prefill: SourcePersonaSeed) => void;  // open the Generate modal, prefilled
  onViewCharacter?: (stockId: string) => void;
}

const scoreVariant = (s: number): "success" | "warning" | "muted" =>
  s >= 0.85 ? "success" : s >= 0.72 ? "warning" : "muted";

export function CastPanel({
  circleKey, arcLabel, seed, videoUrl, platform, onClose, onCreateCharacter, onViewCharacter,
}: CastPanelProps) {
  const toast = useToast();
  const [text, setText] = useState(seed?.description ?? "");
  const [gender, setGender] = useState(seed?.gender ?? "");
  const [ageRange, setAgeRange] = useState(seed?.ageRange ?? "");
  const [picked, setPicked] = useState<string | null>(null);

  // If we have a winning video + platform, for-arc gives us the current cast + auto candidates from
  // the video's appearance persona. Otherwise we run free-text cast search on the typed description.
  const arcQuery = useQuery({
    queryKey: ["cast", "for-arc", circleKey, videoUrl, platform],
    queryFn: () => researchApi.castForArc(circleKey, { videoUrl, platform }),
    enabled: circleKey.trim().length > 0,   // ad-hoc mode (no arc) → skip; use text search only
  });

  const searchQuery = useQuery({
    queryKey: ["cast", "search", text, gender, ageRange],
    queryFn: () => researchApi.castSearch({
      text, gender: gender || null, ageRange: ageRange || null, capability: seed?.capability ?? null,
      platform: seed?.platform ?? null, limit: 12,
    }),
    enabled: text.trim().length > 2,   // don't fire on empty/tiny queries
  });

  // Prefer explicit text-search results; fall back to the for-arc auto candidates.
  const candidates: CastCandidate[] = useMemo(() => {
    if (text.trim().length > 2 && searchQuery.data) return searchQuery.data;
    return arcQuery.data?.candidates ?? [];
  }, [text, searchQuery.data, arcQuery.data]);

  const current = arcQuery.data?.current ?? null;
  const isFetching = searchQuery.isFetching || arcQuery.isFetching;
  const effectivePick = picked ?? (candidates[0]?.stockId ?? null);
  // Ad-hoc mode: no real arc (circleKey empty) → search/preview works, but recording a cast needs an arc.
  const canCast = circleKey.trim().length > 0;

  const cast = useMutation({
    mutationFn: (stockId: string) => {
      const chosen = candidates.find((c) => c.stockId === stockId);
      return researchApi.recordCast({
        circleKey, characterStockId: stockId, castMode: "manual",
        castScore: chosen?.score ?? null,
        sourcePersona: seed ? { ...seed, text } : (text ? { description: text } : null),
      });
    },
    onSuccess: (r) => { toast.success(`Cast recorded (id ${r.castingId}).`); onClose(); },
    onError: (e: unknown) => toast.error(`Cast failed: ${(e as Error).message}`),
  });

  const prefill: SourcePersonaSeed = { description: text, gender: gender || null, ageRange: ageRange || null,
    platform: seed?.platform ?? platform ?? null, capability: seed?.capability ?? "talking_head" };

  // (audit 10-16) a failed search (a 502 from the backend, or the for-arc search named in candidatesError) is said, never
  //  "no character matches"
  const searchError = text.trim().length > 2
    ? (searchQuery.error ? (searchQuery.error as Error).message : null)
    : (arcQuery.error ? (arcQuery.error as Error).message : arcQuery.data?.candidatesError ?? null);
  const noMatches = !isFetching && !searchError && candidates.length === 0;

  return (
    <Modal open onClose={onClose} size="lg"
      title="Cast this arc"
      description={arcLabel ? `Casting for: ${arcLabel}` : `Casting for arc ${circleKey}`}>
      <div className="space-y-4">
        {current && (
          <div className="text-[12px] text-surface-600">
            Currently cast: <span className="font-medium">{current.characterStockId}</span>
            <Badge variant="muted" className="ml-2">{current.castMode}</Badge>
          </div>
        )}

        {/* the source persona / query — editable, prefilled from teardown when available */}
        <Textarea
          label="Source persona (what this arc's winning creator looks/feels like)"
          placeholder="e.g. warm, confident woman in her 30s, casual style, talks straight to camera"
          value={text}
          onChange={(e) => { setText(e.target.value); setPicked(null); }}
          rows={2}
        />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Gender (optional filter)" value={gender}
            onChange={(e) => { setGender(e.target.value); setPicked(null); }}
            options={[{ value: "", label: "Any" }, { value: "F", label: "Female" }, { value: "M", label: "Male" }]} />
          <Select label="Age range (optional boost)" value={ageRange}
            onChange={(e) => { setAgeRange(e.target.value); setPicked(null); }}
            options={[
              { value: "", label: "Any" }, { value: "18-24", label: "18-24" }, { value: "25-34", label: "25-34" },
              { value: "35-44", label: "35-44" }, { value: "45-54", label: "45-54" }, { value: "55+", label: "55+" },
            ]} />
        </div>

        {/* candidates */}
        {isFetching ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : searchError ? (
          <p className="text-sm text-danger">The casting search failed: {searchError}</p>
        ) : noMatches ? (
          <Card><CardContent className="space-y-3 py-8 text-center">
            <p className="text-sm text-surface-600">
              No character matches this persona yet.
            </p>
            <Button onClick={() => onCreateCharacter?.(prefill)}>
              Create a character for this arc
            </Button>
            <p className="text-[11px] text-surface-400">
              Pre-filled from the persona above. After it generates + activates, it becomes castable.
            </p>
          </CardContent></Card>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="text-[12px] text-surface-500">
                {candidates.length} candidate{candidates.length === 1 ? "" : "s"}, ranked by fit
              </div>
              {/* create is ALWAYS available, even with matches */}
              <Button variant="outline" size="sm" onClick={() => onCreateCharacter?.(prefill)}>
                + Create new instead
              </Button>
            </div>
            <div className="grid max-h-[320px] grid-cols-1 gap-2 overflow-y-auto">
              {candidates.map((c) => (
                <button key={c.stockId} type="button"
                  onClick={() => setPicked(c.stockId)}
                  className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left transition ${
                    effectivePick === c.stockId ? "border-surface-900 bg-surface-50" : "border-surface-200 hover:border-surface-300"
                  }`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-[13px] font-medium">{c.displayName ?? c.stockId}</span>
                      <Badge variant={scoreVariant(c.score)}>{(c.score).toFixed(3)}</Badge>
                    </div>
                    <div className="truncate text-[11px] text-surface-500">
                      {[c.gender, c.ageRange, c.capability].filter(Boolean).join(" · ")}
                      {c.castingDesc ? ` — ${c.castingDesc.slice(0, 60)}` : ""}
                    </div>
                  </div>
                  {onViewCharacter && (
                    <Button variant="ghost" size="sm"
                      onClick={(e) => { e.stopPropagation(); onViewCharacter(c.stockId); }}>
                      View
                    </Button>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between gap-2">
        <div className="text-[11px] text-surface-400">
          {!canCast && "Ad-hoc preview — open from an arc to record a cast."}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!canCast || !effectivePick || cast.isPending}
            loading={cast.isPending}
            onClick={() => effectivePick && cast.mutate(effectivePick)}>
            Cast {effectivePick ? (candidates.find((c) => c.stockId === effectivePick)?.displayName ?? "selected") : ""}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
